import { Inbox, RefreshCcw } from 'lucide-react';
import { useEffect, useState } from 'react';

interface CommDelivery {
  id: string;
  triggerKey: string;
  channel: 'email' | 'sms' | 'whatsapp' | 'push';
  recipient: string;
  status: 'queued' | 'sending' | 'delivered' | 'failed_retryable' | 'bounced';
  enqueuedAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  templateVersionId: string | null;
}

interface DeliveriesResponse {
  deliveries: CommDelivery[];
  nextCursor: string | null;
}

const STATUSES: Array<CommDelivery['status'] | 'all'> = [
  'all',
  'queued',
  'sending',
  'delivered',
  'failed_retryable',
  'bounced',
];

function statusClasses(status: CommDelivery['status']): string {
  switch (status) {
    case 'delivered':
      return 'bg-success/10 text-success';
    case 'failed_retryable':
    case 'bounced':
      return 'bg-destructive/10 text-destructive';
    case 'sending':
    case 'queued':
      return 'bg-primary/10 text-primary';
    default:
      return 'bg-secondary text-muted-foreground';
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DeliveriesPage() {
  const [data, setData] = useState<DeliveriesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CommDelivery['status'] | 'all'>('all');
  const [refreshing, setRefreshing] = useState(false);

  async function load() {
    setRefreshing(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await fetch(`/api/comm-deliveries?${params}`, { credentials: 'include' });
      if (!res.ok) {
        setError(`Failed to load (HTTP ${res.status})`);
        return;
      }
      const body = (await res.json()) as DeliveriesResponse;
      setData(body);
    } catch {
      setError('Network error');
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  return (
    <div className="space-y-6 px-4 py-6 md:px-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Deliveries</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Audit log of every email, SMS, WhatsApp, and push notification sent.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={refreshing}
          className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-secondary press disabled:opacity-50"
        >
          <RefreshCcw
            className={`size-3.5 ${refreshing ? 'animate-spin' : ''}`}
            strokeWidth={1.75}
          />
          Refresh
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`rounded-full px-3 py-1 text-xs font-medium press ${
              statusFilter === s
                ? 'bg-primary text-primary-foreground'
                : 'border border-border bg-surface text-muted-foreground hover:bg-secondary'
            }`}
          >
            {s === 'all' ? 'All' : s.replace('_', ' ')}
          </button>
        ))}
      </div>

      {error && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      )}

      {!error && data === null && <p className="text-sm text-muted-foreground">Loading…</p>}

      {data && data.deliveries.length === 0 && (
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <Inbox className="mx-auto size-8 text-muted-foreground" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-muted-foreground">
            No deliveries match the current filter.
          </p>
        </div>
      )}

      {data && data.deliveries.length > 0 && (
        <section className="rounded-2xl bg-surface ring-1 ring-border elevate overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[640px] w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/40">
                  <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Trigger
                  </th>
                  <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Recipient
                  </th>
                  <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground hidden md:table-cell">
                    Channel
                  </th>
                  <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground hidden lg:table-cell">
                    Enqueued
                  </th>
                  <th className="px-5 py-3 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.deliveries.map((d) => (
                  <tr key={d.id}>
                    <td className="px-5 py-3 font-mono text-xs">{d.triggerKey}</td>
                    <td className="px-5 py-3">{d.recipient}</td>
                    <td className="px-5 py-3 hidden md:table-cell capitalize">{d.channel}</td>
                    <td className="px-5 py-3 hidden lg:table-cell num text-xs text-muted-foreground">
                      {formatDateTime(d.enqueuedAt)}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClasses(d.status)}`}
                      >
                        {d.status.replace('_', ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
